import { DateString } from 'common/UrlDate';
import { QueryParams } from 'common/urls/QueryParams';
import { isEmpty, isNil } from 'lodash';
import { useCallback } from 'react';
import { useRouteMatch } from 'react-router';
import { usePersistentDeliveryDates } from 'src/dish-first/usePersistentDeliveryDates';
import { DishFirstSegmentFilter } from 'src/gqlReactTypings.generated.d';
import { Routes } from 'src/shared/Routes';
import { useSearchQueryState } from '../../../../../components/search/store/hooks/useSearchQuery';
import { useCachedDishFirstFilters, useConsumerExploreDishFirstZipCode } from '../../../hooks';
import { useAllCuisinesShowing } from '../../../store';
import { BaseFilterModal } from '../BaseFilterModal';
import { List } from './List';

interface DateFilterModalProps {
  isShowing: boolean;
  onHide: () => void;
  shefId?: string;
}

export const DateFilterModal: React.FC<DateFilterModalProps> = ({ isShowing, onHide, shefId }) => {
  const { showAllCuisines } = useAllCuisinesShowing();
  const query = useSearchQueryState();
  const searchPageMatch = useRouteMatch(Routes.CONSUMER_ORDER_SEARCH);
  const isOnSearchPage = !isNil(searchPageMatch);
  const { filters, availableDeliveryDays, totalShefs } = useCachedDishFirstFilters({
    onChangeFilters: showAllCuisines,
    query: !isEmpty(query) && isOnSearchPage ? query : undefined,
    shefId,
  });
  const { dateFilters, updateQueryParams, zipCode, activeZipCode } = filters;
  const { timeZone } = useConsumerExploreDishFirstZipCode(zipCode ?? activeZipCode);
  const [, setDeliveryDates] = usePersistentDeliveryDates();

  const handleDateChange = useCallback(
    (newDate?: DateString) => {
      setDeliveryDates(newDate ? [newDate] : []);

      const noDateSegmentFilters = filters.segmentFilters.filter(
        (segmentFilter) => segmentFilter !== DishFirstSegmentFilter.NextDay
      );
      if (filters.segmentFilters.length !== noDateSegmentFilters.length) {
        updateQueryParams({
          [QueryParams.SEGMENT_FILTER]: noDateSegmentFilters,
        });
      }
      onHide();
    },
    [filters.segmentFilters, updateQueryParams, setDeliveryDates, onHide]
  );

  return (
    <BaseFilterModal filters={filters} isShowing={isShowing} onHide={onHide} showCTA={false}>
      <List
        availableDeliveryDays={availableDeliveryDays}
        isShefMenu={!!shefId}
        timeZone={timeZone}
        selectedDate={dateFilters[0]}
        totalShefs={totalShefs}
        onDateChange={handleDateChange}
      />
    </BaseFilterModal>
  );
};
